import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [placeId, setPlaceId] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPlaceId(data.place_id);
        if (data.payment_status === "paid") { if (active) setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { if (active) setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (tries < 12 && active) setTimeout(poll, 2000);
      else if (active) setStatus("timeout");
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-sm" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-primary" />
            <h1 className="mt-4 font-serif text-2xl font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={52} className="mx-auto text-secondary" />
            <h1 className="mt-4 font-serif text-3xl font-bold">You're featured! 🎉</h1>
            <p className="mt-2 text-muted-foreground">Your listing now has a Sponsored badge and priority placement across CSRAGo.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/" className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Back to Home</Link>
              <Link to="/explore" className="rounded-full border border-border px-6 py-3 text-sm font-semibold">See it in Explore</Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "error") && (
          <>
            <XCircle size={48} className="mx-auto text-destructive" />
            <h1 className="mt-4 font-serif text-2xl font-bold">Payment not completed</h1>
            <p className="mt-2 text-sm text-muted-foreground">No charge was made. You can try again anytime.</p>
            <Link to="/sponsor" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Try again</Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 size={48} className="mx-auto text-muted-foreground" />
            <h1 className="mt-4 font-serif text-2xl font-bold">Still processing</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your payment is taking a little longer. It'll activate automatically once confirmed.</p>
            <Link to="/" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Back to Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
